import { endOfDay, startOfDay, subDays } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import DateRangePicker, { RangeType } from 'rsuite/DateRangePicker';
import 'rsuite/DateRangePicker/styles/index.css';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Action } from 'src/interfaces/action.interface';
import { Agent } from 'src/interfaces/agent.interface';
import { v4 as uuidv4 } from 'uuid';
import { CallFilters } from '../../interfaces/call.interface';
import { statusesOptions } from '../../stubs/calls.stub';
import { getFirstAndLastFourUUID } from '../../utils/number';
import {
  AutoCompleteMultiSelectBox,
  Option,
} from '../AutoCompleteMultiSelectBox';
import { AutoCompleteSelectBox } from '../AutoCompleteSelectBox';
import { Button } from '../Button';
import { Input } from '../Input';
import { Label } from '../Label';
import { Modal } from '../Modal';
import { SelectBox } from '../SelectBox';

import { Info, Plus, X } from '@phosphor-icons/react';
import { Campaign } from 'src/services/campaigns/campaigns.interface';
import { Tooltip } from '../Tooltip';
import './style.css';

interface CallFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: CallFilters;
  onApplyFilters: (filters: CallFilters) => void;
  agents: Agent[];
  campaigns: Campaign[];
  searchAgents: (name: string) => Promise<{ value: string; label: string }[]>;
  searchCampaigns: (
    name: string,
  ) => Promise<{ value: string; label: string }[]>;
  loading: boolean;
}

const durationComparisonOptions = [
  { value: 'gt', label: '>' },
  { value: 'gte', label: '>=' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '<=' },
  { value: 'eq', label: '=' },
];

export const CallFiltersModal = ({
  isOpen,
  onClose,
  filters,
  onApplyFilters,
  agents,
  campaigns,
  searchAgents,
  searchCampaigns,
  loading,
}: CallFiltersModalProps) => {
  const [localFilters, setLocalFilters] = useState<CallFilters>(filters);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(() => {
    const end = endOfDay(new Date());
    const start = startOfDay(subDays(end, 27));
    return [start, end];
  });
  const [actions, setActions] = useState<Action[]>([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [callContextRows, setCallContextRows] = useState<
    Array<{ id: string; key: string; value: string }>
  >([{ id: uuidv4(), key: '', value: '' }]);
  const { get } = useAuthedRequest();

  const agentsOptions = [
    { value: 'All Agents' },
    ...agents.map((agent: Agent) => ({
      value: agent.id as string,
      label: agent.name || getFirstAndLastFourUUID(agent.id as string),
    })),
  ];

  const campaignOptions = [
    { value: 'All Campaigns' },
    ...campaigns.map((campaign) => ({
      value: campaign.referenceId,
      label: campaign.name
        ? `${campaign.name} (${getFirstAndLastFourUUID(campaign.referenceId)})`
        : getFirstAndLastFourUUID(campaign.referenceId),
    })),
  ];

  const customRanges = [
    {
      label: 'Last 28 days',
      value: [startOfDay(subDays(new Date(), 27)), endOfDay(new Date())],
      closeOverlay: true,
    },
    {
      label: 'Last 7 days',
      value: [startOfDay(subDays(new Date(), 6)), endOfDay(new Date())],
      closeOverlay: true,
    },
    {
      label: 'Yesterday',
      value: [
        startOfDay(subDays(new Date(), 1)),
        endOfDay(subDays(new Date(), 1)),
      ],
      closeOverlay: true,
    },
    {
      label: 'Today',
      value: [startOfDay(new Date()), endOfDay(new Date())],
      closeOverlay: true,
    },
  ];

  const fetchActions = useCallback(
    async (query?: string) => {
      setActionsLoading(true);
      try {
        const queryParams = new URLSearchParams({
          size: '50',
          filters: JSON.stringify({
            scope: 'agent',
            ...(query && query.length > 0 && { label: query }),
          }),
        });

        const { items } = await get(`/actions?${queryParams.toString()}`);
        setActions(items);
      } catch (error) {
        console.error('Error fetching actions:', error);
      } finally {
        setActionsLoading(false);
      }
    },
    [get],
  );

  const actionOptions: Option[] = actions.map((action) => ({
    value: JSON.stringify({ type: action.type, label: action.label }),
    label: action.label || action.type,
  }));

  const selectedActionOptions: Option[] = (
    localFilters.successful_actions || []
  ).map((action) => ({
    value: JSON.stringify({ type: action.type, label: action.label }),
    label: action.label || action.type,
  }));

  const handleSuccessfulActionsChange = (options: Option[]) => {
    const successfulActions = options.map((option) => {
      const parsed = JSON.parse(option.value);
      return { type: parsed.type, label: parsed.label };
    });
    updateLocalFilters({ successful_actions: successfulActions });
  };

  const handleApply = () => {
    const callContextJson = getCallContextJson();
    const updatedFilters: CallFilters = {
      ...localFilters,
      call_context:
        Object.keys(callContextJson).length > 0 ? callContextJson : undefined,
      ...(dateRange &&
      dateRange[0] instanceof Date &&
      dateRange[1] instanceof Date
        ? {
            start_time: {
              comparison: 'btwn',
              value: [dateRange[0].toISOString(), dateRange[1].toISOString()],
            },
          }
        : { start_time: null }),
    };
    onApplyFilters(updatedFilters);
    onClose();
  };

  const handleReset = () => {
    const end = endOfDay(new Date());
    const start = startOfDay(subDays(end, 27));
    const resetFilters: CallFilters = {
      agent: 'All Agents',
      workflow_reference_id: 'All Campaigns',
      status: 'All Statuses',
      call_id: '',
      contact_number: '',
      from_number: '',
      to_number: '',
      start_time: {
        comparison: 'btwn',
        value: [start.toISOString(), end.toISOString()],
      },
      duration: undefined,
      successful_actions: [],
      transcript_keyword: '',
      call_context: undefined,
    };
    setLocalFilters(resetFilters);
    setDateRange([start, end]);
    setCallContextRows([{ id: uuidv4(), key: '', value: '' }]);
  };

  const updateLocalFilters = (newFilters: Partial<CallFilters>) => {
    setLocalFilters((prev: CallFilters) => ({ ...prev, ...newFilters }));
  };

  useEffect(() => {
    if (isOpen && actions.length === 0) {
      fetchActions();
    }
  }, [isOpen, actions.length, fetchActions]);

  // Sync filters when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalFilters(filters);
      if (filters.call_context) {
        const rows = Object.entries(filters.call_context).map(
          ([key, value]) => ({
            id: uuidv4(),
            key,
            value: String(value),
          }),
        );
        if (rows.length > 0) {
          setCallContextRows(rows);
        }
      }
    }
  }, [isOpen, filters]);

  const addCallContextRow = () => {
    setCallContextRows((prev) => [
      ...prev,
      { id: uuidv4(), key: '', value: '' },
    ]);
  };

  const removeCallContextRow = (id: string) => {
    setCallContextRows((prev) => prev.filter((row) => row.id !== id));
  };

  const updateCallContextRow = (
    id: string,
    field: 'key' | 'value',
    value: string,
  ) => {
    setCallContextRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    );
  };

  const getCallContextJson = (): Record<string, string> => {
    const context: Record<string, string> = {};
    callContextRows.forEach((row) => {
      if (row.key.trim()) {
        context[row.key.trim()] = row.value;
      }
    });
    return context;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Filter Calls"
      className="lg:max-w-[800px]"
      actionButton={
        <div className="flex gap-2">
          <Button onClick={handleReset} color="default">
            Reset
          </Button>
          <Button onClick={handleApply} disabled={loading}>
            Apply Filters
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Date Range */}
        <div>
          <Label htmlFor="period">Period</Label>
          <DateRangePicker
            format="MM/dd/yyyy hh:mm aa"
            showMeridiem
            className="w-full"
            value={dateRange as [Date, Date]}
            onChange={(value) => {
              if (!value) {
                setDateRange([null, null]);
                return;
              }
              setDateRange(value as [Date, Date]);
            }}
            ranges={customRanges as RangeType[]}
          />
        </div>

        {/* Agent and Status Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="agents">Agents</Label>
            <AutoCompleteSelectBox
              key={`agent-${localFilters.agent}`}
              defaultValue={agentsOptions.find(
                (agent) => agent.value === localFilters.agent,
              )}
              fetchOptions={searchAgents}
              onChange={(value) => updateLocalFilters({ agent: value })}
              variant="outlined"
              color="primary"
              size="medium"
              disabled={loading}
              defaultOptions={agentsOptions}
              allowRemoveOption={false}
            />
          </div>

          <div>
            <Label htmlFor="status">Statuses</Label>
            <SelectBox
              options={statusesOptions}
              value={statusesOptions.find(
                (status) => status.value === localFilters.status,
              )}
              variant="outlined"
              color="primary"
              size="medium"
              onChange={(value) => updateLocalFilters({ status: value })}
            />
          </div>
        </div>

        {/* Call ID and Phone Numbers Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              label="Call ID"
              type="text"
              value={localFilters.call_id || ''}
              onChange={(e) => updateLocalFilters({ call_id: e.target.value })}
            />
          </div>

          <div>
            <Input
              label={
                <>
                  Contact Number{' '}
                  <Tooltip
                    content="The number of the contact who received or made the call."
                    zIndex={50}
                  >
                    <Info className="w-4 h-4 inline-block ml-1" />
                  </Tooltip>
                </>
              }
              type="text"
              value={localFilters.contact_number}
              onChange={(e) =>
                updateLocalFilters({ contact_number: e.target.value })
              }
            />
          </div>
        </div>

        {/* Duration Filter */}
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="w-full">
              <Label htmlFor="duration">Duration (seconds)</Label>

              <div className="flex">
                <SelectBox
                  options={durationComparisonOptions}
                  value={durationComparisonOptions.find(
                    (option) =>
                      option.value === localFilters.duration?.comparison,
                  )}
                  variant="outlined"
                  color="primary"
                  size="medium"
                  onChange={(value) =>
                    updateLocalFilters({
                      duration: {
                        comparison: value,
                        value: localFilters.duration?.value || 0,
                      },
                    })
                  }
                  className="w-full rounded-r-none"
                />

                <Input
                  type="number"
                  placeholder="0"
                  value={localFilters.duration?.value || ''}
                  onChange={(e) =>
                    updateLocalFilters({
                      duration: {
                        comparison: localFilters.duration?.comparison || 'gt',
                        value: parseInt(e.target.value) || 0,
                      },
                    })
                  }
                  variant="outlined"
                  color="primary"
                  size="medium"
                  className="w-full rounded-l-none ml-[-1px] py-[0.53rem]"
                  fullWidth={true}
                />
              </div>
            </div>

            {/* Successful Actions Filter */}
            <div className="w-full">
              <Label htmlFor="successful-actions">Successful Actions</Label>
              <AutoCompleteMultiSelectBox
                fetchOptions={fetchActions}
                selectedOptions={selectedActionOptions}
                options={actionOptions}
                onSelectionChange={handleSuccessfulActionsChange}
                variant="outlined"
                color="primary"
                size="medium"
                placeholder="Select completed actions..."
                noOptionsText="No actions found"
                disabled={actionsLoading}
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Transcript Keyword */}
          <div className="w-full">
            <Input
              label="Transcript Keyword"
              type="text"
              placeholder="Search in transcript..."
              value={localFilters.transcript_keyword}
              onChange={(e) =>
                updateLocalFilters({ transcript_keyword: e.target.value })
              }
            />
          </div>

          {/* campaign ref id */}
          <div className="w-full">
            <Label htmlFor="campaign">Campaign</Label>
            <AutoCompleteSelectBox
              key={`campaign-${localFilters.workflow_reference_id}`}
              defaultValue={campaignOptions.find(
                (campaign) =>
                  campaign.value === localFilters.workflow_reference_id,
              )}
              fetchOptions={searchCampaigns}
              onChange={(value) =>
                updateLocalFilters({ workflow_reference_id: value })
              }
              variant="outlined"
              color="primary"
              size="medium"
              disabled={loading}
              defaultOptions={campaignOptions}
              allowRemoveOption={false}
            />
          </div>
        </div>

        {/* Call Context */}
        <div>
          <Label htmlFor="call-context">Call Context</Label>
          <div className="space-y-2">
            {callContextRows.map((row) => (
              <div key={row.id} className="flex gap-2 items-start">
                <div className="flex-1">
                  <Input
                    type="text"
                    placeholder="Key"
                    value={row.key}
                    onChange={(e) =>
                      updateCallContextRow(row.id, 'key', e.target.value)
                    }
                    variant="outlined"
                    color="primary"
                    size="medium"
                  />
                </div>
                <div className="flex-1">
                  <Input
                    type="text"
                    placeholder="Value"
                    value={row.value}
                    onChange={(e) =>
                      updateCallContextRow(row.id, 'value', e.target.value)
                    }
                    variant="outlined"
                    color="primary"
                    size="medium"
                  />
                </div>
                <Button
                  variant="text"
                  color="error"
                  size="medium"
                  onClick={() => removeCallContextRow(row.id)}
                  disabled={callContextRows.length === 1}
                  aria-label="Remove row"
                  className="hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            ))}
            <Button
              variant="text"
              color="primary"
              size="medium"
              onClick={addCallContextRow}
            >
              <Plus className="w-5 h-5" />
              <span>Add row</span>
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
